import {cn, cva} from 'platform/shared';

import {ChangeEvent, ForwardedRef, forwardRef} from 'react';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../../types/FormControlProps';
import {HTMLAutocomplete} from '../HTMLAutocomplete';

export interface NativeInputElementProps
  extends FormControlProps<string | null, HTMLInputElement>, TestIdProps {
  id: string;
  isAutoFocused?: boolean;
  autoComplete?: HTMLAutocomplete;
  placeholder?: string | Nullish;
  type?: string;
  maxLength?: number;
  isInvalid: boolean;
  isDisabled: boolean;
  'data-name'?: string;
  // Accessibility attributes
  role?: string;
  'aria-label'?: string;
  'aria-valuenow'?: number;
  'aria-valuemin'?: number;
  'aria-valuemax'?: number;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  className?: string;
}

const inputVariants = cva(
  'w-full border-none bg-transparent text-[length:var(--text-size-small)] font-[var(--text-weight-default)] leading-[var(--text-leading-small)] text-[var(--color-primary)] outline-none placeholder:text-[var(--color-tertiary)] autofill:[text-fill-color:var(--color-primary)] autofill:[box-shadow:0_0_0_300px_var(--color-palettes-yellow-20-100)_inset,0_0_0_100px_var(--color-palettes-yellow-20-100)] autofill:focus:[text-fill-color:var(--color-primary)] autofill:focus:[box-shadow:0_0_0_300px_var(--color-palettes-yellow-20-100)_inset,0_0_0_100px_var(--color-palettes-yellow-20-100)] [&:-webkit-autofill]:[-webkit-text-fill-color:var(--color-primary)] [&:-webkit-autofill]:[-webkit-box-shadow:0_0_0_300px_var(--color-palettes-yellow-20-100)_inset,0_0_0_100px_var(--color-palettes-yellow-20-100)] [&:-webkit-autofill:focus]:[-webkit-text-fill-color:var(--color-primary)] [&:-webkit-autofill:focus]:[-webkit-box-shadow:0_0_0_300px_var(--color-palettes-yellow-20-100)_inset,0_0_0_100px_var(--color-palettes-yellow-20-100)] focus:[border-color:none] focus:shadow-none focus:outline-none focus-visible:[border-color:none] focus-visible:shadow-none focus-visible:outline-none disabled:[-webkit-text-fill-color:var(--color-primary)] disabled:placeholder:[-webkit-text-fill-color:var(--color-tertiary)]',
  {
    variants: {
      isDisabled: {
        true: 'cursor-not-allowed',
        false: '',
      },
    },
    defaultVariants: {
      isDisabled: false,
    },
  }
);

/**
 * @private
 * This component is a platform only bulding block
 * Do not use this anywhere outside of platform
 */
export const PlatformInputElement = forwardRef(PlatformInputElementComponent);
export function PlatformInputElementComponent(
  props: NativeInputElementProps,
  ref: ForwardedRef<HTMLInputElement>
) {
  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const newValue = event.target.value || null;
    props.onChange?.(newValue, event);
  }

  const autofocusAttribute = props.isAutoFocused ? {autofocus: 'autofocus'} : {};

  return (
    <input
      {...autofocusAttribute}
      className={cn(inputVariants({isDisabled: props.isDisabled}), props.className)}
      id={props.id}
      ref={ref}
      type={props.type}
      name={props.name}
      maxLength={props.maxLength}
      autoComplete={props.autoComplete || undefined}
      disabled={props.isDisabled || undefined}
      placeholder={props.placeholder ?? undefined}
      value={props.value || ''}
      onChange={handleChange}
      onBlur={props.onBlur}
      onFocus={props.onFocus}
      onPaste={props.onPaste}
      onKeyDown={props.onKeyDown}
      autoFocus={props.isAutoFocused}
      data-name={props['data-name']}
      data-testid={suffixTestId('input', props)}
      // Accessibility attributes
      role={props.role}
      aria-label={props['aria-label']}
      aria-valuenow={props['aria-valuenow']}
      aria-valuemin={props['aria-valuemin']}
      aria-valuemax={props['aria-valuemax']}
      aria-invalid={props['aria-invalid']}
      aria-describedby={props['aria-describedby']}
      aria-required={props.isRequired || undefined}
      aria-disabled={props.isDisabled || undefined}
    />
  );
}
